// Contrato de eventos socket — fuente de verdad para client y server.
//
// NOTA tipos WebRTC: RTCSessionDescriptionInit / RTCIceCandidateInit son
// types globales del browser (lib.dom.d.ts). El server compila con
// `lib: ["ES2023"]` sin DOM, así que los redeclaramos local con la
// forma mínima que necesitamos del contrato de signaling. Esto hace
// que el shared module sea portable a cualquier target (Node, Bun,
// Workers) sin arrastrar todo lib.dom. En el cliente, los types reales
// del browser son estructuralmente compatibles con estas firmas y la
// asignación funciona transparente.
export type RTCSessionDescriptionInit = {
    type: "offer" | "answer" | "pranswer" | "rollback"
    sdp?: string
}
export type RTCIceCandidateInit = {
    candidate?: string
    sdpMid?: string | null
    sdpMLineIndex?: number | null
    usernameFragment?: string | null
}

export const SOCKET_EVENTS = {
    // ── Cliente → Servidor ────────────────────────────────
    PLAYER_MOVE: "player:move",
    PLAYER_MOVE_CLICK: "player:move_click",
    PLAYER_INDEX_UPDATE: "player:index_update",
    SPAWN_REQUEST: "spawn:request", // confirmo modelo de encarnación
    RECONFIGURE_REQUEST: "reconfigure:request", // pido volver al vacío
    LINK_REACH: "link:reach", // pongo el cursor sobre alguien
    LINK_RELEASE: "link:release", // suelto
    WEBRTC_OFFER: "webrtc:offer",
    WEBRTC_ANSWER: "webrtc:answer",
    WEBRTC_ICE: "webrtc:ice",

    // ── Servidor → Cliente ────────────────────────────────
    ROOM_STATE: "room:state",
    PLAYER_JOINED: "player:joined",
    PLAYER_MOVED: "player:moved",
    PLAYER_MOVE_CLICKED: "player:move_clicked",
    PLAYER_INDEX_UPDATED: "player:index_updated",
    PLAYER_LEFT: "player:left",
    PLAYER_ANCHOR: "player:anchor", // alguien entró/salió de modo anclaje
    SPAWN_EVENT: "spawn:event", // descenso/materialización (self + peers)
    VOID_RETURN: "void:return", // confirmación de RECONFIGURE_REQUEST
    AUTH_ERROR: "auth:error",

    // Llave doble (bilateral handshake)
    LINK_INCOMING: "link:incoming", // X te está mirando
    LINK_INCOMING_LOST: "link:incoming_lost", // X dejó de mirarte
    LINK_RECIPROCATED: "link:reciprocated", // ambos se conectaron, sealing 3s arranca
    LINK_SEALED: "link:sealed", // 3s completados, enlace activo
    LINK_ABORTED: "link:aborted", // se rompió (release o movimiento)

    // WebRTC signaling (server reenvía a peer)
    WEBRTC_OFFER_INCOMING: "webrtc:offer_incoming",
    WEBRTC_ANSWER_INCOMING: "webrtc:answer_incoming",
    WEBRTC_ICE_INCOMING: "webrtc:ice_incoming",

    // Sistema
    PING: "ping",
    PONG: "pong",
} as const

export type SocketEventName =
    (typeof SOCKET_EVENTS)[keyof typeof SOCKET_EVENTS]

export type Vector3Tuple = [number, number, number]
export type QuaternionTuple = [number, number, number, number]

// ── Sprint 3 — Modelos base de encarnación ───────────────
// Tres formas que el Tripulante puede elegir al descender al Domo Cero.
// El modelo es estético; mecánicamente todos los avatares son equivalentes.
export type AvatarModel = "cristal" | "plasma" | "biopolimero"

// ── Sprint 5 — Polaridad del avatar ──────────────────────
// Eje complementario al modelo. Define la firma vibracional del
// Tripulante en el Domo: alfa (eléctrico/masculino) impulsa la geometría
// hacia ángulos agudos y rotación horaria; omega (magnético/femenino)
// la suaviza y rota en sentido antihorario.
export type AvatarPolarity = "alfa" | "omega"

export interface PlayerSnapshot {
    id: string // Clerk user id
    displayName: string
    avatarUrl?: string
    position: Vector3Tuple
    target: Vector3Tuple | null // hacia dónde se está moviendo (point-and-click)
    rotation: QuaternionTuple
    siliconIndex: number // 0-100, índice promedio del Escáner
    isAnchored: boolean // >10s sin moverse → modo meditación, intocable
    joinedAt: number
    // Sprint 3 — Estado de encarnación.
    // - model = null + isIncarnated = false: vacío, esperando elección.
    // - model elegido + isIncarnated = false: descendiendo (animación viva).
    // - model elegido + isIncarnated = true: ya en el Domo Cero, móvil.
    model: AvatarModel | null
    // Sprint 5 — Polaridad ronda paralela al modelo. Solo es null
    // mientras el Tripulante no haya completado su primer descenso
    // (o si reseteó su firma desde la Cámara de Reconfiguración).
    polarity: AvatarPolarity | null
    isIncarnated: boolean
}

// ── Sprint 3/5 — Spawn / Encarnación ─────────────────────
export interface SpawnRequestPayload {
    model: AvatarModel
    polarity: AvatarPolarity
}

// Sprint 5 — Reconfiguración. El cliente envía RECONFIGURE_REQUEST sin
// payload cuando el Tripulante elige "Elegir avatar" en el menú. El
// server limpia su firma persistida, lo saca del campo (PLAYER_LEFT a
// peers) y le emite VOID_RETURN para que vuelva a phase="void".

// El server emite este evento a self + peers visibles tras un SPAWN_REQUEST
// válido. Los clientes corren la animación de descenso (haz de luz desde
// y=20 hasta el pad central) y al terminar materializan el avatar con el
// modelo elegido. El servidor toma `position` como ancla del descenso.
export interface SpawnEventBroadcast {
    id: string
    model: AvatarModel
    polarity: AvatarPolarity
    position: Vector3Tuple // pad de aterrizaje (server lo decide)
    descentMs: number // duración del haz hasta el pad
    materializeMs: number // duración del shimmer/sparkles del solidificado
    serverTs: number // referencia temporal para sincronizar entre clientes
    // Sprint 5 — Cuando el server detecta una firma persistida y dispara
    // auto-encarnación, marca este flag. El cliente lo usa para no
    // mostrar el selector y arrancar directo la animación.
    auto?: boolean
}

// ── Llave doble ─────────────────────────────────────────
export interface LinkReachPayload {
    targetId: string
}

export interface LinkIncomingBroadcast {
    fromId: string
}

export interface LinkIncomingLostBroadcast {
    fromId: string
}

export interface LinkReciprocatedBroadcast {
    peerId: string
    sealsInMs: number // duración del anillo de carga
}

export interface LinkSealedBroadcast {
    peerId: string
    isInitiator: boolean // el de id menor inicia la WebRTC offer
}

export interface LinkAbortedBroadcast {
    peerId: string
    reason:
        | "released"
        | "moved"
        | "disconnect"
        | "anchored"
        | "out_of_range"
        | "manual"
}

export interface AnchorBroadcast {
    id: string
    isAnchored: boolean
}

// ── WebRTC signaling ────────────────────────────────────
export interface WebrtcOfferPayload {
    to: string
    sdp: RTCSessionDescriptionInit
}

export interface WebrtcAnswerPayload {
    to: string
    sdp: RTCSessionDescriptionInit
}

export interface WebrtcIcePayload {
    to: string
    candidate: RTCIceCandidateInit
}

export interface WebrtcOfferIncoming {
    from: string
    sdp: RTCSessionDescriptionInit
}

export interface WebrtcAnswerIncoming {
    from: string
    sdp: RTCSessionDescriptionInit
}

export interface WebrtcIceIncoming {
    from: string
    candidate: RTCIceCandidateInit
}

export interface PlayerMoveClickPayload {
    target: Vector3Tuple
    ts: number
}

export interface PlayerMoveClickedBroadcast {
    id: string
    target: Vector3Tuple
    ts: number
}

export interface PlayerMovePayload {
    position: Vector3Tuple
    rotation: QuaternionTuple
    ts: number
}

export interface PlayerMovedBroadcast {
    id: string
    position: Vector3Tuple
    rotation: QuaternionTuple
    ts: number
}

export interface PlayerIndexUpdatePayload {
    siliconIndex: number
}

export interface PlayerIndexUpdatedBroadcast {
    id: string
    siliconIndex: number
}

export interface PlayerLeftBroadcast {
    id: string
}

export interface RoomStatePayload {
    roomId: string
    selfId: string
    players: PlayerSnapshot[]
    serverTs: number
}
